import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import TableFooter from "../../components/Pagination/TableFooter"

import TableContactList from "./Table/Contact/TableContactList"
import { useDispatch, useSelector } from "react-redux"
import { fetchContactsReq, getContactByFilterList, getContactStatuses, } from "../../features/customer/customerSlice"
import { fetchContactGroupsDropdownInfo } from "../../features/contactGroups/contactGroups"
import { fetchAllAffiliatesDropdown } from "../../features/affiliates/affiliates"
import { downloadCustomersCsv } from "../../features/customersExport/customersExport"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import DebounceInput from "../../components/DebouncedSearch/DebouncedSearch"
import PlusIcon from "../../assets/images/icons/PlusIcon.svg"
import Button from "../../components/Button/Button"
import { useNavigate } from "react-router-dom"
import { StorageGetItem, USER } from "../../lib/storage"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, clearListCache, buildCacheKey } from "../../utils/listCache"
import { initQueryData } from "../../components/Filters/helper"
import Filters from "../../components/Filters/Filters"
import useURLState from "../../hooks/useURLState"
import StatusTabs from "../../components/StatusTabs/StatusTabs"
import AppliedFilters from "../../components/AppliedFilters/AppliedFilters"
import { TABLE_CARD } from "../../components/Table/tableCardClass"
import { handleTableId } from "../../helpers/helper"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { CONTACT_FINANCING, CONTACT_FINANCING_PENDING } from "../../config/featureFlagKeys"
import { REQUEST_ERRORS_LISTS } from "../../constants/requestErrorFlags"
import { withFinancingFilter } from "./financedFilter"
import FinancingToggle from "./FinancingToggle"

const Contacts = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isContacts = true
  const { triggerBar } = useMyContextLoading()

  const {
    page,
    setPage,
    limit,
    search,
    filters,
    filterListId,
    sortField,
    sortOrder,
    status,
    financed,
    setMultiple,
  } = useURLState({
    page: { type: 'number', default: 1 },
    limit: { type: 'number', default: 10 },
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
    filterListId: { type: 'string', default: '' },
    sortField: { type: 'string', default: '' },
    sortOrder: { type: 'string', default: '' },
    status: { type: 'string', default: '' },
    financed: { type: 'string', default: '' },
  })

  // WS 2211251. "Financed" toggle; ignored while CONTACT_FINANCING is off.
  const financingEnabled = useSelector(selectFeatureFlag(CONTACT_FINANCING))
  const financedOn = financingEnabled && financed === '1'
  // WS 2311820. With CONTACT_FINANCING_PENDING it also takes 'pending'; off = as above.
  const financingPendingEnabled = useSelector(selectFeatureFlag(CONTACT_FINANCING_PENDING))
  const listStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_LISTS))
  const listStrictRef = useRef(listStrict)
  listStrictRef.current = listStrict
  const financingValue = financingPendingEnabled && financed === 'pending' ? financed : (financedOn ? '1' : '')
  const financingOptions = [
    ['', 'All'], ['pending', 'Pending'], ...(financingEnabled ? [['1', 'Paid']] : []),
  ]

  // Stable serialized filters for dependency tracking
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])

  // ── Preserve the list position across a round-trip to a contact detail ──────────────
  // Page/filters live in the URL. Opening a contact navigates to /contact/:id; returning
  // via the nav menu lands on a BARE /contacts, which resets to page 1 — so a rep bulk-
  // editing (e.g. switching every GreenMoney account to LawPay) is thrown back to page 1
  // after every single edit. Persist the list state and restore it on a bare return so they
  // resume where they left off. Session-scoped (clears when the tab closes); a URL that
  // carries its own params (browser back/forward, shared link) is left untouched.
  const LIST_STATE_KEY = 'contactsListState'
  const canPersistList = useRef(false)

  useEffect(() => {
    const bareUrl = window.location.search.replace(/^\?/, '').length === 0
    if (bareUrl) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(LIST_STATE_KEY) || 'null')
        const meaningful = saved && (saved.page > 1 || saved.search || saved.filters?.length ||
          saved.filterListId || saved.sortField || saved.status || saved.financed)
        if (meaningful) setMultiple(saved, { replace: true })
      } catch { /* ignore malformed persisted state */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist on change, skipping the initial mount pass so we never clobber the saved state
  // with defaults before the restore above has read it.
  useEffect(() => {
    if (!canPersistList.current) { canPersistList.current = true; return }
    try {
      sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify({
        page, limit, search, filters, filterListId, sortField, sortOrder, status, financed,
      }))
    } catch { /* sessionStorage unavailable — non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, filtersKey, filterListId, sortField, sortOrder, status, financed])

  // Local state for UI filter editing (not persisted until Apply)
  const [requestFilters, setRequestFilters] = useState(() =>
    filters.length > 0 ? filters : [initQueryData]
  )
  const [filterData, setFilterData] = useState({ filters: [], utils: [] })

  // Sync local filters with URL when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setRequestFilters(filters.length > 0 ? filters : [initQueryData])
  }, [filtersKey])

  // Local state (non-URL)
  const [pageCount, setPageCount] = useState('')
  const [isUpdate, setIsUpdate] = useState(false)
  const [handleDelete, setHandleDelete] = useState('')
  const [onCreateContact, setOnCreateContact] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [contacts, setContacts] = useState([])
  // Customers had no loading state at all, so first load and every filter change
  // showed an empty table body with no feedback. Leads already does this.
  const [contactsLoading, setContactsLoading] = useState(true)
  const [statuses, setStatuses] = useState([])
  const [contactGroupsMap, setContactGroupsMap] = useState({})
  const [usersMap, setUsersMap] = useState({})
  const [currentUser, setCurrentUser] = useState(null)
  const [statusCounts, setStatusCounts] = useState({})
  const [financingCounts, setFinancingCounts] = useState(null)
  const [userStatuses, setUserStatuses] = useState([])
  const [statusesLoaded, setStatusesLoaded] = useState(false)
  const [countsLoaded, setCountsLoaded] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const isFetching = useRef(false)

  const getStatuses = async () => {
    const cached = getListCache('customers:statusOptions')
    if (cached) {
      setStatuses(cached.statuses)
      setUserStatuses(cached.userStatuses)
      setStatusesLoaded(true)
    }
    triggerBar(true)
    try {
      const result = await dispatch(getContactStatuses())
      const statuses = result?.payload?.data
      setStatuses(statuses)
      const filtered = (statuses || []).filter(s => s.value !== 'newLead' && s.value !== 'duplicate')
      const userStatuses = filtered.map(s => ({ _id: s._id, label: s.label, value: s.value, bgColor: s.bgColor, textColor: s.textColor }))
      setUserStatuses(userStatuses)
      setStatusesLoaded(true)
      setListCache('customers:statusOptions', { statuses, userStatuses })
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    } finally {
      triggerBar(false)
    }
  }

  const getContactGroups = async () => {
    triggerBar(true)
    try {
      const result = await dispatch(fetchContactGroupsDropdownInfo())
      const groups = result?.payload?.data || []
      // Create map: { id: name } for quick lookup
      const map = groups.reduce((acc, group) => {
        acc[group.value] = group.label
        return acc
      }, {})
      setContactGroupsMap(map)
    } catch (error) {
      console.error('Failed to fetch contact groups:', error)
    } finally {
      triggerBar(false)
    }
  }

  const getUsers = async () => {
    triggerBar(true)
    try {
      const result = await dispatch(fetchAllAffiliatesDropdown({ affiliatesOnly: false }))
      const users = result?.payload?.data || []
      // Create map: { id: { firstName, lastName, contactGroups } } for assignee lookup
      const map = users.reduce((acc, user) => {
        acc[user.value] = {
          _id: user.value,
          firstName: user.firstName,
          lastName: user.lastName,
          contactGroups: user.contactGroups || []
        }
        return acc
      }, {})
      setUsersMap(map)
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      triggerBar(false)
    }
  }

  const loadCurrentUser = async () => {
    const user = await StorageGetItem(USER)
    setCurrentUser(user)
  }

  useEffect(() => {
    getStatuses()
    getContactGroups()
    getUsers()
    loadCurrentUser()
  }, [])

  const customersCacheGuard = useRef(true)
  useEffect(() => {
    if (customersCacheGuard.current) {
      customersCacheGuard.current = false
      return
    }
    clearListCache('customers')
  }, [isUpdate, handleDelete, onCreateContact])

  // Fetch contacts - dependencies are primitives only
  useEffect(() => {
    if (filterListId) return

    const cacheKey = buildCacheKey('customers', { page, limit, search, filtersKey, sortField, sortOrder, financingValue })

    const applyData = ({ users, pageCount, totalCount, statusCounts, financingCounts }) => {
      setContacts(users)
      setContactsLoading(false)
      setPageCount(pageCount)
      setTotalCount(totalCount)
      setStatusCounts(statusCounts)
      setFinancingCounts(financingCounts || null)
      setCountsLoaded(true)
    }

    const fetchData = async () => {
      const cached = getListCache(cacheKey)
      if (cached) applyData(cached)
      else setContactsLoading(true)
      triggerBar(true)
      try {
        const { role } = await StorageGetItem(USER)
        const offset = (page - 1) * limit
        const currentFilters = filters.length > 0 ? filters : [initQueryData]
        const hasFilters = currentFilters.some(f => f.filterType?.length > 0)
        const apiFilters = withFinancingFilter(hasFilters ? currentFilters.filter(f => f.filterType?.length > 0) : "", financingValue)

        const response = await dispatch(fetchContactsReq({
          limit,
          offset,
          filters: apiFilters,
          searchQuery: search,
          byCreator: role !== 'customer',
          sortField,
          sortOrder,
        }))
        if (listStrictRef.current && response?.error) {
          showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch contacts')
          setContactsLoading(false)
          triggerBar(false)
          return
        }
        const fresh = {
          users: response?.payload?.data?.users,
          pageCount: response?.payload?.data?.meta?.pageCount,
          totalCount: response?.payload?.data?.meta?.totalCount,
          statusCounts: response?.payload?.data?.meta?.statusCounts || {},
          financingCounts: response?.payload?.data?.meta?.financingCounts || null,
        }
        applyData(fresh)
        setListCache(cacheKey, fresh)
      } catch (error) {
        showSnackBar("error", error)
        console.error('An unexpected error occurred:', error)
      }
      setContactsLoading(false)
      triggerBar(false)
    }

    fetchData()
  }, [page, limit, search, filtersKey, filterListId, sortField, sortOrder, financingValue, isUpdate, handleDelete, onCreateContact, dispatch])

  // Fetch by filter list
  useEffect(() => {
    if (!filterListId) return
    if (isFetching.current) return

    const fetchData = async () => {
      isFetching.current = true
      triggerBar(true)
      try {
        const { role } = await StorageGetItem(USER)
        const offset = (page - 1) * limit

        const response = await dispatch(getContactByFilterList({
          filterListId,
          limit,
          offset,
          searchQuery: search,
          byCreator: role !== 'customer',
          sortField,
          sortOrder,
        }))
        if (listStrictRef.current && response?.error) {
          showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch contacts')
          setContactsLoading(false)
          triggerBar(false)
          isFetching.current = false
          return
        }
        setContacts(response?.payload?.data?.users)
        setContactsLoading(false)
        setPageCount(response?.payload?.data?.meta?.pageCount)
        setTotalCount(response?.payload?.data?.meta?.totalCount)
      } catch (error) {
        showSnackBar("error", error)
        console.error('An unexpected error occurred:', error)
      }
      triggerBar(false)
      isFetching.current = false
    }

    fetchData()
  }, [page, limit, search, filterListId, sortField, sortOrder, isUpdate, handleDelete, onCreateContact, dispatch])

  // Handler for search (resets page, preserves saved filter)
  const handleSearch = useCallback((value) => {
    if (value?.length >= 3 || value?.length === 0) {
      setMultiple({
        search: value,
        page: 1,
      })
    }
  }, [setMultiple])

  // Handler for saved filter selection
  const handleFilterListSelect = useCallback((id) => {
    setMultiple({
      filterListId: id,
      filters: [],
      page: 1,
    })
  }, [setMultiple])

  // Handler for pagination
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage)
  }, [setPage])

  const handleLimitChange = useCallback((newLimit) => {
    setMultiple({
      limit: newLimit,
      page: 1,
    })
  }, [setMultiple])

  const applyFilters = useCallback((newFilters) => {
    setRequestFilters(newFilters)
    setMultiple({
      filters: newFilters[0]?.filterType?.length > 0 ? newFilters : [],
      filterListId: '',
      page: 1,
    })
  }, [setMultiple])

  // Trigger refetch by changing handleDelete (used for delete etc.)
  const triggerRefetch = useCallback(() => {
    setHandleDelete(Date.now().toString())
  }, [])

  const handleSort = useCallback((field, order) => {
    setMultiple({
      sortField: field,
      sortOrder: order,
      page: 1,
    })
  }, [setMultiple])

  const handleStatusChange = (statusValue) => {
    if (statusValue === '') {
      const newFilters = requestFilters.filter(f => f.filterType !== 'userStatuses')
      setMultiple({
        status: '',
        filters: newFilters.length > 0 ? newFilters : [],
        page: 1,
      })
      setRequestFilters(newFilters.length > 0 ? newFilters : [initQueryData])
    } else {
      const statusObj = userStatuses.find(s => s.value === statusValue)
      const statusId = statusObj?._id || statusValue
      const statusFilter = { fieldName: 'userStatuses', operator: [statusId], value: '', filterType: 'userStatuses' }
      const otherFilters = requestFilters.filter(f => f.filterType && f.filterType !== 'userStatuses')
      const newFilters = [...otherFilters, statusFilter]
      setMultiple({
        status: statusValue,
        filters: newFilters,
        page: 1,
      })
      setRequestFilters(newFilters)
    }
  }

  useEffect(() => {
    const statusFilter = filters.find(f => f.filterType === 'userStatuses')
    if (statusFilter && Array.isArray(statusFilter.operator) && statusFilter.operator.length === 1) {
      const statusId = statusFilter.operator[0]
      const statusObj = userStatuses.find(s => s._id === statusId)
      const statusValue = statusObj?.value || statusId
      if (status !== statusValue) {
        setMultiple({ status: statusValue })
      }
    } else if (!statusFilter && status !== '') {
      setMultiple({ status: '' })
    }
  }, [filtersKey])

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      if (filterListId) {
        await downloadCustomersCsv({
          filterListId,
          searchQuery: search,
          sortField,
          sortOrder,
        })
      } else {
        // Same massaging the list fetch applies before calling the API
        const currentFilters = filters.length > 0 ? filters : [initQueryData]
        const hasFilters = currentFilters.some(f => f.filterType?.length > 0)
        const apiFilters = withFinancingFilter(hasFilters ? currentFilters.filter(f => f.filterType?.length > 0) : "", financingValue)

        const params = {
          searchQuery: search,
          sortField,
          sortOrder,
        }
        if (Array.isArray(apiFilters) && apiFilters.length > 0) {
          params.filters = apiFilters
        }
        await downloadCustomersCsv(params)
      }
    } catch (error) {
      showSnackBar("error", error?.message || "Export failed — please try again")
    } finally {
      setIsExporting(false)
    }
  }

  const copyEnrollmentLink = () => {
    if (!currentUser?._id) {
      showSnackBar("error", "Unable to generate enrollment link")
      return
    }
    const baseUrl = window.location.origin
    const affiliateId = handleTableId(currentUser._id)
    // Carry the office the link was generated from, so a prospect of a multi-office
    // affiliate is never asked to pick one they have no way to choose between.
    const activeOfficeId = localStorage.getItem('activeOfficeId')
    const officeQuery = activeOfficeId ? `?office=${encodeURIComponent(activeOfficeId)}` : ''
    const link = `${baseUrl}/enrollment/${affiliateId}${officeQuery}`
    navigator.clipboard.writeText(link)
    showSnackBar("success", "Enrollment link copied to clipboard")
  }

  return (
    <div className="">
      <ContainerPage>
        <div className="flex justify-between items-center">
          <PageTitle title="Customers List" />
          <div className="flex gap-4">
            {currentUser?.role === 'customer' && (
              <Button
                classes="flex h-9 items-center"
                type="outlined"
                disabled={isExporting}
                onClick={handleExportCsv}
              >
                {isExporting ? 'Exporting…' : 'Export to CSV'}
              </Button>
            )}
            {(localStorage.getItem('activeOfficeId') || currentUser?.contactGroups?.length > 0) && (
              <Button
                classes="flex h-9 items-center"
                type="outlined"
                onClick={copyEnrollmentLink}
              >
                Self Enrollment
              </Button>
            )}
            <Button classes="flex h-9 items-center" onClick={() => navigate("/contact/add-contact", {
              replace: true,
              state: { isCreate: true }
            })}>
              <p className="flex">
                <img src={PlusIcon} alt="plus" />
                <p>Add Contact</p>
              </p>
            </Button>
          </div>
        </div>
        <div className="mb-4">
          <StatusTabs
            counts={statusCounts}
            statuses={userStatuses}
            activeStatus={status}
            onStatusChange={handleStatusChange}
            loading={!statusesLoaded || !countsLoaded}
            skeletonCount={5}
          />
        </div>
        <div className={TABLE_CARD}>
          <div className="flex flex-row justify-between items-center px-5 py-4">
            <div className="flex flex-col">
              <div className="flex flex-row">
                <DebounceInput customClassName={'mr-5'} value={search} setSearchValue={handleSearch}
                               placeholder="Search..."/>
                <Filters fetchByFilterList={handleFilterListSelect} fetchByFilters={applyFilters}
                         requestFilters={requestFilters} flow="contacts" isContacts={isContacts}
                         filterListId={filterListId} setFilterListId={handleFilterListSelect}
                         onFilterDataLoaded={setFilterData}/>
                {financingPendingEnabled && !filterListId && (
                  <FinancingToggle options={financingOptions} value={financingValue} counts={financingCounts}
                    onChange={(value) => setMultiple({ financed: value, page: 1 })} />
                )}
                {financingEnabled && !financingPendingEnabled && !filterListId && (
                  <label className="ml-5 flex items-center gap-2 text-sm text-text-primary cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={financedOn}
                      onChange={(e) => setMultiple({ financed: e.target.checked ? '1' : '', page: 1 })}
                    />
                    Financed
                  </label>
                )}
              </div>
            </div>
          </div>
          <div className="px-5">
            <AppliedFilters
              requestFilters={requestFilters}
              filterDefs={filterData.filters} utilDefs={filterData.utils}
              onRemove={(index) => {
                const active = requestFilters.filter(f => f.fieldName || f.operator || f.value)
                const updated = active.filter((_, i) => i !== index)
                setRequestFilters(updated.length > 0 ? updated : [initQueryData])
                setMultiple({ filters: updated.length > 0 ? updated : [], page: 1 })
              }}
              onClearAll={() => {
                setRequestFilters([initQueryData])
                setMultiple({ filters: [], page: 1 })
              }}
            />
          </div>
          <TableContactList data={contacts} statuses={statuses} setHandleDelete={setHandleDelete} isContacts={isContacts}
                            loading={contactsLoading}
                            skeletonRows={limit}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            contactGroupsMap={contactGroupsMap}
                            usersMap={usersMap}
                            triggerUpdate={(e) => {
                              e && triggerRefetch()
                            }}/>
          <TableFooter page={page} limit={limit} total={totalCount} pageCount={pageCount}
            setPage={handlePageChange} setLimit={handleLimitChange} />
        </div>
      </ContainerPage>
    </div>
  )
}

export default Contacts
